import * as Sentry from "@sentry/react";
import Keycloak from "keycloak-js";
import React from "react";
import { SplashScreen } from "../../common/SplashScreen.js";
import { AuthContext } from "./AuthContext.js";
import { hasITPRestrictions } from "../featureDetection.js";

interface Props extends React.PropsWithChildren<{}> {
  keycloak: Keycloak;
}

export const AuthWebProvider = ({ children, keycloak }: Props) => {
  const [isReady, setIsReady] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  React.useEffect(() => {
    function setIsAuthenticatedState() {
      setIsAuthenticated(keycloak.authenticated ?? false);
    }

    async function bootstrap() {
      try {
        keycloak.onReady = () => {
          setIsReady(true);
        };
        keycloak.onAuthError = setIsAuthenticatedState;
        keycloak.onAuthLogout = setIsAuthenticatedState;
        keycloak.onAuthRefreshError = setIsAuthenticatedState;
        keycloak.onTokenExpired = setIsAuthenticatedState;
        keycloak.onAuthSuccess = setIsAuthenticatedState;
        keycloak.onAuthRefreshSuccess = setIsAuthenticatedState;
        await keycloak.init({
          onLoad: "check-sso",
          checkLoginIframe: !hasITPRestrictions(),
          silentCheckSsoRedirectUri: `${location.origin}/${
            import.meta.env.VITE_ROUTER_BASENAME
              ? import.meta.env.VITE_ROUTER_BASENAME + "/"
              : ""
          }silent-check-sso.html`,
          silentCheckSsoFallback: true,
        });
      } catch (e) {
        if (e instanceof Error)
          console.error("Failed to initialize keycloak: ", e.message);
        Sentry.captureException(e);
      }
    }

    void bootstrap();
  }, [keycloak]);

  if (!isReady) return <SplashScreen />;

  return (
    <AuthContext.Provider
      value={{
        isReady,
        isAuthenticated,
        belongsToGroup: (groupName: string) =>
          keycloak.idTokenParsed?.groups
            ? keycloak.idTokenParsed.groups.includes(groupName)
            : false,
        auth: keycloak,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
